/**
 * THE CASH BOOK FROM THE CLIENT'S CONNECTED BOOKS (ADR-0024 T5.4).
 *
 * Offered beside the upload, on a work that still needs its cash book, when the client's books are connected and
 * the client consented to Leveri reading their ledger. Reading is the server's act — the period's ledger and the
 * trial balance as at the day before it, or a plain sentence saying why not — and the screen offers it and shows
 * what came back. The system's name is the connection's own; the browser holds no list of systems.
 *
 * Secondary, never the primary: "Start the work" stays the one saturated element on this screen.
 */
import { useEffect, useState } from "react";
import { ConnectionsResponse, WorkActionResponse, type WorkFile } from "@leveri/contracts";
import { Button, Notice, Reading } from "../../file/index.js";
import { call, failureMessage } from "../../api.js";

/** What a cash book read from the books needs the client to have consented to. */
const LEDGER = ["ledger.transactions", "ledger.trial_balance"];

export interface ConnectedCashBookProps {
  workId: string;
  clientId: string;
  onWork: (work: WorkFile) => void;
  onOutcome: (outcome: string) => void;
}

export function ConnectedCashBook({ workId, clientId, onWork, onOutcome }: ConnectedCashBookProps) {
  const [system, setSystem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    call("GET", `/api/clients/${clientId}/connections`, ConnectionsResponse)
      .then((r) => {
        const usable = r.connections.find((c) => c.status === "ACTIVE" && LEDGER.every((kind) => c.evidenceKinds.includes(kind)));
        if (live) setSystem(usable ? usable.systemName : null);
      })
      // Not knowing is not offering: the upload is always there.
      .catch(() => { if (live) setSystem(null); });
    return () => { live = false; };
  }, [clientId]);

  if (!system) return null;

  async function read() {
    if (busy) return;
    setBusy(true);
    setFailure(null);
    try {
      const result = await call("POST", `/api/work/${workId}/connected-records`, WorkActionResponse, {});
      onOutcome(result.outcome);
      onWork(result.work);
    } catch (error) {
      setFailure(failureMessage(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mb-5" data-testid="connected-cash-book">
      <div className="flex flex-wrap items-center gap-3.5">
        <Button variant="secondary" disabled={busy} onClick={() => void read()}>Read the cash book from {system}</Button>
        <span className="text-[13px] text-stone">The client's ledger for this period and its opening balance, read from their books.</span>
      </div>
      {busy && <div className="mt-2"><Reading>Reading the books…</Reading></div>}
      {failure && <div className="mt-2"><Notice>{failure}</Notice></div>}
    </div>
  );
}
